<template>
	<section class="">
		<!-- Кнопка «Назад» над заголовком (конвенция проекта — button--card button--m)
		     → на предыдущую страницу (шагов у входа нет). Если возвращаться некуда
		     (прямой заход, разлогин с закрытой страницы) — ведёт на главную.
		     Логотип живёт только в панели. -->
		<button class="button button--card button--m d-inline-flex flex-v-center gap-small mn-b-medium align-self-start" @click="goBack">
			<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1em" width="1em"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 480 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-370.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"></path></svg>
			{{ backTarget.isHome ? t('auth.signUp.home') : t('auth.signUp.back') }}
		</button>

		<h3 class="mn-b-medium">
			{{ t('auth.signIn.title') }}
			<br>
			<span class="fw-semi">{{ t('auth.signIn.subtitle') }}</span>
		</h3>

		<!-- Select -->
		<div
			v-if="availableTabs.length > 1"
			class="mn-b-small"
		>
			<Tab
				v-model:selected="tabAuth"
				:tabs="availableTabs"
				classTabBar="w-100"
			/>
		</div>
		<!-- Form -->
		<div class="pos-relative">
		<!-- Phone -->
			<transition name="slide-fade">
				<div v-show="tabAuth === 'phone'" class="mn-b-thin">
					<FieldPhone
						:model-value="auth.state.user.phone"
						@change="(event) => auth.state.user.phone = event"
						:dropdownOptions="{
							showDialCodeInSelection: true,
							showFlags: true,
							showDialCodeInList: true,
							tabIndex: -1
						}"
						:validCharactersOnly="true"
						:validation="phoneValidation"
						mode="national"
						:inputOptions="{placeholder: t('auth.signIn.phonePlaceholder'), type: 'tel'}"
						class="input mn-b-thin"
					/>

				</div>
			</transition>
			<transition name="slide-fade">
				<div v-show="tabAuth === 'email'" class="mn-b-thin">
					<Field
						v-model:field="auth.state.user.email"
						:placeholder="t('auth.signIn.emailPlaceholder')"
						:validation="emailValidation"
						:tabIndex="-1"
						class="input"
					/>
				</div>
			</transition>
		</div>

		<!-- Password -->
		<Field
			v-model:field="auth.state.user.password"
			type="password"
			:validation="passswordValidation"
			:placeholder="t('auth.signIn.passwordPlaceholder')"
			class="input mn-b-small"
		/>
		<!-- Links -->
		<div class="w-100 mn-b-medium">
			<router-link
				:to="{name: 'Reset Password', query: { returnUrl: route.query?.returnUrl}}"
				class="underline d-block t-blue"
			>
				{{ t('auth.signIn.forgotPassword') }}
			</router-link>
		</div>
		<!-- Button -->
		<Button
			:submit="onSubmit"
			class="w-100 button--accent"
		>
			{{ t('auth.signIn.signin') }}
		</Button>

		<!-- Ссылка на регистрацию — прибита к низу карточки -->
		<div class="w-100 mn-t-auto pd-t-medium">
			<router-link :to="{name: 'Sign Up', query: { returnUrl: route.query?.returnUrl}}" class="underline d-block t-blue">{{ t('auth.signIn.signup') }}</router-link>
		</div>

		<!-- <Button
			:submit="onSubmitApple"
			:callback="redirectTo"
			class="mn-b-thin bg-inverse t-inverse"
		>
			{{ t('auth.signIn.signin_apple') }}
		</Button> -->
	</section>
</template>

<script setup>
// Import components
import Tab           from '@martyrs/src/design-system/components/Tab/Tab.vue'
import Field         from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone    from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Button        from '@martyrs/src/design-system/components/Button/Button.vue'
// Import libs
import { computed, onMounted, ref, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
// Import store
import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js'
import { mixins } from '@martyrs/src/modules/core/core.client.js'
import * as auth from '@martyrs/src/modules/auth/views/store/auth.js'
// Import validation
import * as inputsValidation from '@martyrs/src/modules/auth/views/validations/inputs.validation'
// Import utils
import { resolveAuthBackTarget } from '@martyrs/src/modules/auth/views/utils/back-target.js'

// Get store
const store = useStore()
const core = store.core
const { isModuleInstalled } = mixins.useGlobalMixins()

// Init validation
const phoneValidation = ref(null)
const passswordValidation = ref(null)
const emailValidation = ref(null)
// Accessing router
const route = useRoute()
const router = useRouter()
// Localization
const { t } = useI18n({
	useScope: 'global'
})
// Куда ведёт кнопка над заголовком. Считаем один раз при открытии страницы:
// история после монтирования компонента уже не меняется.
const backTarget = resolveAuthBackTarget(router)

function goBack() {
	if (backTarget.isHome) router.push(backTarget.path)
	else router.back()
}
// Accessing state
const availableTabs = computed(() => {
    const tabs = [
        { name: t('auth.signIn.phone'), value: 'phone' },
        { name: t('auth.signIn.email'), value: 'email' }
    ];
    const excludeMethods = core.state?.options?.auth?.authMethodsExclude || [];
    return tabs.filter(tab => !excludeMethods.includes(tab.value));
});

const tabAuth = ref(availableTabs.value.length ? availableTabs.value[0].value : '');

// Идентификатор из query (?email= / ?phone=): человек пришёл со страницы
// регистрации, где адрес уже оказался занят. Подставляем его и открываем нужную
// вкладку — остаётся ввести пароль. Читаем в setup: FieldPhone берёт modelValue
// на своём монтировании. Email вперёд телефона — как на регистрации.
for (const type of ['email', 'phone']) {
	const raw = typeof route.query?.[type] === 'string' ? route.query[type].trim() : '';
	if (!raw) continue;
	if (!availableTabs.value.some(tab => tab.value === type)) continue;
	auth.state.user[type] = raw;
	tabAuth.value = type;
	break;
}

const loadExternalScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

onMounted(async () => {
	await loadExternalScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
});
// Methods
async function onSubmit() {
	try {
	  if (tabAuth.value === 'phone') await inputsValidation.validateInputs(
			phoneValidation,
			inputsValidation.validatePhone,
			auth.state.user.phone,
			'Некорректный телефон'
		)
		if (tabAuth.value === 'email') await inputsValidation.validateInputs(
			emailValidation,
			inputsValidation.validateEmail,
			auth.state.user.email,
			'Некорректный email'
		)
	} catch (error) {
		console.log(error)
		throw new Error
	}

	// COSTIL PLEASE GOD MAKE IT GOOD, FUKEN INPUT COMPONENT RETURN OBJECT NUT NUMBER
	const userCopy = {...auth.state.user};
	// COSTIL PLEASE GOD MAKE IT GOOD, FUKEN INPUT COMPONENT RETURN OBJECT NUT NUMBER

	await auth.actions.login(userCopy, tabAuth.value)

	redirectTo()
}

async function onSubmitApple() {
  try {
    window.AppleID.auth.init({
      clientId: 'com.thecommune.app.signin',
      scope: 'email name',
      redirectURI: 'https://thecommunephuket.com/auth/signin',
      usePopup: true,
    });

    let response = await window.AppleID.auth.signIn();

    if (response) {
      console.log(response);
      await auth.actions.login(response, 'apple');
    }

  } catch (error) {
    console.error(error);
    throw new Error('Error during Apple authentication');
  }
}

function redirectTo () {
	if (!auth.state.user._id) {
		throw new Error('AUTH_STATE_NOT_INITIALIZED')
	}

	if (route.query?.returnUrl) {
		// Прокидываем весь query (кроме returnUrl) на целевую страницу: флоу
		// вроде ?buyPack=… должны пережить вход, а не теряться здесь.
		const { returnUrl, ...extraQuery } = route.query
		router.push({
			path: returnUrl,
			query: {
				...extraQuery,
				afterAuth: 'true'
			}
		})
	} else if (auth.state.access.roles?.includes('ROLE_ADMIN') && isModuleInstalled('backoffice')) {
		router.push({
			path: '/backoffice',
			query: {
				afterAuth: 'true'
			}
		})
	} else {
		// Дефолт пост-входа — из конфига проекта (options.auth.redirectAfterAuth),
		// иначе профиль пользователя (дефолт martyrs).
		const configured = core.state?.options?.auth?.redirectAfterAuth
		if (configured) {
			router.push({
				path: configured,
				query: {
					afterAuth: 'true'
				}
			})
		} else {
			router.push({
				name: 'User Profile',
				params: {
					_id: auth.state.user._id
				},
				query: {
					afterAuth: 'true'
				}
			})
		}
	}
}
</script>

<style lang="scss">

	.input-error {
	  border: 1px solid red;
	  box-shadow: 0 0 3px 0 red;
	}
</style>